/**
 * One model's output: editable prompt, timing, then blocks.
 *
 * Raw text is always reachable. The parsed block view is a convenience, and
 * when a model returns something we cannot parse, that IS the finding — so the
 * parse error is shown next to the raw text rather than swallowed.
 */

import { useState } from "react";
import { TableBlock, isTableHtml } from "@/components/TableBlock";
import type { ModelInfo, RunResult } from "@/lib/types";

export function ResultColumn({ model, prompt, onPrompt, result, running }: {
  model: ModelInfo;
  prompt: string;
  onPrompt: (v: string) => void;
  result: RunResult | null;
  running: boolean;
}) {
  const [view, setView] = useState<"blocks" | "raw">("blocks");
  const [showPrompt, setShowPrompt] = useState(false);

  return (
    <div className="flex flex-col rounded-lg ring-1 ring-fh-border-soft bg-white">
      <div className="flex items-center justify-between border-b border-fh-border-soft px-4 py-2.5">
        <span className="text-fh-body font-medium text-fh-text-strong">{model.label}</span>
        <div className="flex items-center gap-3 text-fh-meta">
          {result?.ok && (
            <span className="text-fh-text-muted">
              {(result.ms / 1000).toFixed(1)}s
              {result.usage?.completion_tokens ? ` · ${result.usage.completion_tokens} tok` : ""}
              {result.blocks.length ? ` · ${result.blocks.length} blocks` : ""}
            </span>
          )}
          <button onClick={() => setShowPrompt((v) => !v)} className="text-fh-text-secondary hover:text-fh-text-strong">
            Prompt
          </button>
          <div className="flex rounded ring-1 ring-fh-border-soft">
            {(["blocks", "raw"] as const).map((v) => (
              <button key={v} onClick={() => setView(v)}
                className={`px-2 py-0.5 ${view === v ? "bg-fh-navy text-white" : "text-fh-text-secondary"}`}>
                {v}
              </button>
            ))}
          </div>
        </div>
      </div>

      {showPrompt && (
        <div className="border-b border-fh-border-soft p-3">
          <textarea
            value={prompt}
            onChange={(e) => onPrompt(e.target.value)}
            rows={3}
            className="w-full resize-y rounded border border-fh-border p-2 font-mono text-fh-meta"
          />
          <p className="mt-1 text-fh-meta text-fh-text-muted">
            Sent verbatim. The two models expect different prompt grammars — {model.label}'s
            default is the one its authors document.
          </p>
        </div>
      )}

      <div className="min-h-[280px] max-h-[62vh] overflow-auto p-4 text-fh-body">
        {running && <p className="text-fh-text-muted">Running…</p>}
        {!running && !result && <p className="text-fh-text-muted">No run yet.</p>}
        {result && !result.ok && (
          <p className="whitespace-pre-wrap text-fh-danger text-fh-meta">{result.error}</p>
        )}
        {result?.ok && result.parseError && (
          <p className="mb-3 rounded bg-fh-badge-amber-bg p-2 text-fh-meta text-fh-badge-amber-text">
            Could not parse this model's layout format ({result.parseError}). Raw output shown.
          </p>
        )}
        {result?.ok && view === "raw" && (
          <pre className="whitespace-pre-wrap break-words font-mono text-fh-meta text-fh-text-body">{result.raw}</pre>
        )}
        {result?.ok && view === "blocks" && (
          <ol className="space-y-2">
            {result.blocks.map((b, i) => (
              <li key={i} className="border-l-2 border-fh-border pl-3">
                <div className="flex items-center gap-2 text-fh-meta uppercase tracking-wide text-fh-text-muted">
                  <span>{b.category}</span>
                  {b.bbox && <span className="font-mono normal-case">
                    [{b.bbox.map((n) => Math.round(n)).join(", ")}]
                  </span>}
                </div>
                {isTableHtml(b.text) ? (
                  <TableBlock html={b.text} />
                ) : (
                  <div className="whitespace-pre-wrap break-words text-fh-text-body">{b.text}</div>
                )}
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}
